import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import FormContainer from '../../components/ui/FormContainer';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import { loadFormData, saveProgress, getUserEmail } from '../../utils/onboardingApi';

const DealInformationFormStep1 = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    dealFundName: '',
    assetType: 'Multifamily',
    dealFundStructure: '506c',
    raiseType: 'Equity',
    briefDescription: ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Load saved form data from API on component mount
  useEffect(() => {
    const userEmail = getUserEmail();

    if (!userEmail) {
      console.warn('No email found. User may need to access via email link.');
      setIsLoading(false);
      return;
    }

    // Try to load from API
    loadFormData('deal-info-1')
      .then(data => {
        if (Object.keys(data).length > 0) {
          setFormData(prev => ({
            ...prev,
            dealFundName: data?.dealFundName || prev?.dealFundName,
            assetType: data?.assetType || prev?.assetType,
            dealFundStructure: data?.dealFundStructure || prev?.dealFundStructure,
            raiseType: data?.raiseType || prev?.raiseType,
            briefDescription: data?.briefDescription || prev?.briefDescription
          }));
        } else {
          // Fallback to localStorage for legacy data
          const savedData = localStorage.getItem('dealFormData');
          if (savedData) {
            try {
              const parsed = JSON.parse(savedData);
              setFormData(prev => ({...prev, ...parsed}));
            } catch (e) {
              console.error('Failed to parse localStorage data:', e);
            }
          }
        }
      })
      .catch(err => {
        console.error('Error loading form data:', err);
        // Fallback to localStorage on API error
        const savedData = localStorage.getItem('dealFormData');
        if (savedData) {
          try {
            const parsed = JSON.parse(savedData);
            setFormData(prev => ({...prev, ...parsed}));
          } catch (e) {
            console.error('Failed to parse localStorage data:', e);
          }
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  // Auto-save form data to API whenever it changes (silent background operation)
  useEffect(() => {
    if (isLoading) return; // Don't save during initial load

    const timeoutId = setTimeout(() => {
      saveProgress('deal-info-1', {
        dealFundName: formData?.dealFundName,
        assetType: formData?.assetType,
        dealFundStructure: formData?.dealFundStructure,
        raiseType: formData?.raiseType,
        briefDescription: formData?.briefDescription
      })
        .then(() => {
          console.log('Auto-saved form data');
        })
        .catch(err => {
          console.error('Failed to auto-save:', err);
          // Fallback to localStorage on API error
          localStorage.setItem('dealFormData_backup', JSON.stringify(formData));
        });
    }, 1000); // Debounce for 1 second

    return () => clearTimeout(timeoutId);
  }, [formData, isLoading]);

  const assetTypeOptions = [
    { value: 'Multifamily', label: 'Multifamily' },
    { value: 'New Construction', label: 'New Construction' },
    { value: 'Industrial', label: 'Industrial' },
    { value: 'Self-Storage', label: 'Self-Storage' },
    { value: 'Private Credit', label: 'Private Credit' },
    { value: 'Hospitality', label: 'Hospitality' },
    { value: 'Other', label: 'Other' }
  ];

  const dealStructureOptions = [
    { value: '506c', label: '506c' },
    { value: '506b', label: '506b' },
    { value: 'Reg A', label: 'Reg A' },
    { value: 'Reg CF', label: 'Reg CF' }
  ];

  const raiseTypeOptions = [
    { value: 'Debt', label: 'Debt' },
    { value: 'Equity', label: 'Equity' },
    { value: 'Both', label: 'Both' }
  ];

  const handleInputChange = (field) => (value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleDescriptionChange = (e) => {
    const value = e?.target?.value || '';
    setFormData(prev => ({
      ...prev,
      briefDescription: value
    }));
    
    // Clear error when user starts typing
    if (errors?.briefDescription) {
      setErrors(prev => ({
        ...prev,
        briefDescription: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.dealFundName?.trim()) {
      newErrors.dealFundName = 'Deal/Fund name is required';
    }
    
    if (!formData?.assetType) {
      newErrors.assetType = 'Please select an asset type';
    }
    
    if (!formData?.dealFundStructure) {
      newErrors.dealFundStructure = 'Please select a deal/fund structure';
    }
    
    if (!formData?.raiseType) {
      newErrors.raiseType = 'Please select what you want to raise';
    }

    if (!formData?.briefDescription?.trim()) {
      newErrors.briefDescription = 'Brief description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleContinue = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Save to API before navigating
      await saveProgress('deal-info-1', {
        dealFundName: formData?.dealFundName,
        assetType: formData?.assetType,
        dealFundStructure: formData?.dealFundStructure,
        raiseType: formData?.raiseType,
        briefDescription: formData?.briefDescription,
        createdAt: new Date().toISOString()
      });

      // Navigate to next step (document upload)
      navigate('/document-upload-step-2');
    } catch (error) {
      console.error('Failed to save form data:', error);

      // Show user-friendly error message
      const errorMessage = error.message || 'Failed to save form data. Please try again.';
      alert(errorMessage);

      // Don't let the error propagate to ErrorBoundary
      return;
    }
  };

  // const handleBack = () => {
  //   navigate('/deals-dashboard-empty-state');
  // };

  return (
    <FormContainer maxWidth="max-w-lg">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-8">
          {/* <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            type="button"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button> */}
          <h1 className="text-2xl font-semibold text-gray-900">
            Tell us about your deal or fund
          </h1>
        </div>

        {/* Form Fields */}
        <div className="space-y-6">
          {/* Deal/Fund Name */}
          <Input
            label="Deal/Fund Name"
            placeholder="Add text"
            value={formData?.dealFundName || ''}
            onChange={(e) => handleInputChange('dealFundName')(e?.target?.value)}
            error={errors?.dealFundName}
            required
          />

          {/* Asset Type */}
          <Select
            label="Asset Type"
            options={assetTypeOptions}
            value={formData?.assetType || ''}
            onChange={handleInputChange('assetType')}
            error={errors?.assetType}
            required
          />

          {/* Deal/Fund Structure */}
          <Select
            label="Deal/Fund Structure"
            options={dealStructureOptions}
            value={formData?.dealFundStructure || ''}
            onChange={handleInputChange('dealFundStructure')}
            error={errors?.dealFundStructure}
            required
          />

          {/* I want to raise */}
          <Select
            label="I want to raise:"
            options={raiseTypeOptions}
            value={formData?.raiseType || ''}
            onChange={handleInputChange('raiseType')}
            error={errors?.raiseType}
            required
          />

          {/* Brief Description */}
          <div className="space-y-2">
            <label 
              htmlFor="briefDescription"
              className="text-sm font-medium text-foreground block"
            >
              Brief Description of your offering
              <span className="text-destructive ml-1">*</span>
            </label>
            <textarea
              id="briefDescription"
              placeholder="Enter your USP here"
              value={formData?.briefDescription || ''}
              onChange={handleDescriptionChange}
              rows={6}
              className="w-full px-3 py-2 border border-input rounded-md bg-white text-black text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 resize-none"
            />
            {errors?.briefDescription && (
              <p className="text-sm text-destructive">
                {errors?.briefDescription}
              </p>
            )}
          </div>
        </div>

        {/* Continue Button */}
        <div className="pt-6">
          <Button
            onClick={handleContinue}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            size="lg"
          >
            Continue
          </Button>
        </div>
      </div>
    </FormContainer>
  );
};

export default DealInformationFormStep1;